import type { PlanId } from '../../../types/subscription';
import type { TabButtonProps } from '../../common/TabButton';
import TabButton from '../../common/TabButton';
import type {
  SubscriptionFeature,
  SubscriptionPeriod,
} from '../SubscriptionCard';
import SubscriptionCard from '../SubscriptionCard';

export interface SubscriptionPlan {
  id: PlanId;
  name: string;
  description: string;
  badgeLabel?: string;
  price: Record<SubscriptionPeriod, string>;
  features: SubscriptionFeature[];
}

export interface SubscriptionSelectorProps {
  // matching to the tab button constraints i.e. only 2 or 3
  plans:
    | [SubscriptionPlan, SubscriptionPlan]
    | [SubscriptionPlan, SubscriptionPlan, SubscriptionPlan];
  period: SubscriptionPeriod;
  selectedPlanId?: PlanId;
  currentPlanId?: PlanId;
  billingNote?: string;
  onSelectPlan: (planId: PlanId) => void;
  onSubscribe: (planId: PlanId) => void;
  onPeriodChange: (period: SubscriptionPeriod) => void;
  disabled?: boolean;
  buttonLabel?: string;
  activeButtonLabel?: string;
  loading?: boolean;
}

const SubscriptionSelector = ({
  plans,
  period,
  selectedPlanId,
  currentPlanId,
  billingNote,
  onSelectPlan,
  onSubscribe,
  onPeriodChange,
  disabled = false,
  buttonLabel,
  activeButtonLabel,
  loading = false,
}: SubscriptionSelectorProps) => {
  const getCardProps = (plan: SubscriptionPlan) => ({
    subscriptionName: plan.name,
    subscriptionDescription: plan.description,
    subscriptionPrice: '$' + plan.price[period],
    billingNote: billingNote
      ?.replaceAll(
        '{discountedPrice}',
        '$' +
          (
            Math.round(Math.round(Number(plan.price[period]) * 100) / 2) / 100
          ).toFixed(2)
      )
      .replaceAll(
        '{regularPrice}',
        '$' + Number(plan.price[period]).toFixed(2)
      ),
    subscriptionPeriod: period,
    subscriptionFeatures: plan.features,
    badgeLabel: plan.badgeLabel,
    isSelected: plan.id === selectedPlanId,
    onClick: () => onSubscribe(plan.id),
    disabled: disabled || plan.id === currentPlanId,
    buttonLabel:
      plan.id === currentPlanId
        ? 'CURRENT PLAN'
        : plan.id === selectedPlanId
          ? activeButtonLabel || buttonLabel
          : buttonLabel,
    loading: plan.id === selectedPlanId && loading,
  });

  const tabOptions: TabButtonProps['options'] =
    plans.length === 3
      ? [
          { label: plans[0].name, value: plans[0].id },
          { label: plans[1].name, value: plans[1].id },
          { label: plans[2].name, value: plans[2].id },
        ]
      : [
          { label: plans[0].name, value: plans[0].id },
          { label: plans[1].name, value: plans[1].id },
        ];

  const activePlan =
    plans.find((plan) => plan.id === selectedPlanId) ?? plans[0];

  return (
    <div>
      <div className="w-full md:w-1/2 mx-auto">
        <TabButton
          disabled={disabled}
          size="compact"
          options={[
            { label: 'Monthly', value: 'monthly' },
            {
              label: (
                <div className="whitespace-nowrap">
                  Annual{' '}
                  <span className="text-bittersweet font-semibold">
                    (Save up to 16%)
                  </span>
                </div>
              ),
              value: 'yearly',
            },
          ]}
          onChange={(newValue) => {
            onPeriodChange(newValue as SubscriptionPeriod);
            if (newValue !== period && window.innerWidth < 1024) {
              onSelectPlan('professional');
            }
          }}
          value={period}
        />
      </div>

      <div className="lg:hidden mt-8">
        <SubscriptionCard
          {...getCardProps(activePlan)}
          headerSlot={
            <TabButton
              disabled={disabled}
              size="heading"
              emphasis="high"
              options={tabOptions}
              value={selectedPlanId || ''}
              onChange={(newValue) => onSelectPlan(newValue as PlanId)}
            />
          }
        />
      </div>

      <div className="hidden lg:grid grid-cols-3 justify-items-center gap-6 mt-8">
        {plans.map((plan) => (
          <SubscriptionCard key={plan.id} {...getCardProps(plan)} />
        ))}
      </div>
    </div>
  );
};

export default SubscriptionSelector;
